import { useState } from "react";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import SavedFiltersView from "./SavedFiltersView";

function SavedFilters({ controller }) {
  const callEvaApi = useEvaApi();
  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    name: "",
    default: false,
  });

  /**
   * UseEffect for fetching the saved filters when the component mounts
   */
  useCustomEffect(() => {
    fetchSavedFilters(true);
  }, []);

  /**
   * This method will fetch the saved filters from the API
   * @param {boolean} loadDefault whether to load the default filter into the datatable, which should only happen on mount.
   * After a save, delete or default toggle, only the list is refreshed, so the current view of the user stays the same.
   */
  async function fetchSavedFilters(loadDefault = false) {
    await callEvaApi(
      new EvaApiCall("user_saved_filters")
        .setParams({ key: controller.config.getDataTableKey() })
        .setLoadingGroup(
          `datatable-${controller.config.getDataTableKey()}-saved-filters-fetch`,
        )
        .setAlertError(true)
        .setAlertSuccess(false)
        .setOnSuccess((response) => {
          setSavedFilters(response.data.data.records);
          if (!loadDefault) return;

          const defaultFilter = response.data.data.records.find(
            (f) => f.default,
          );
          onLoadFilter(defaultFilter ?? null);
        }),
    );
  }

  /**
   * This method will load the given filter into the datatable
   * @param {object|null} savedFilter
   */
  function onLoadFilter(savedFilter) {
    const clear = !savedFilter || savedFilter.id === activeFilter;
    setActiveFilter(clear ? null : savedFilter.id);
    controller.onLoadSavedFilter(clear ? null : savedFilter);
    controller.savedFiltersDropdown.setOpen(false);
  }

  /**
   * This method will toggle the default state of the given filter
   * @param {object} savedFilter
   */
  async function onToggleDefault(savedFilter) {
    await callEvaApi(
      new EvaApiCall(`user_saved_filters/${savedFilter.id}`)
        .setLoadingGroup(
          `datatable-${controller.config.getDataTableKey()}-saved-filters-toggle-default-${savedFilter.id}`,
        )
        .setMethod("PUT")
        .setData({ default: !savedFilter.default })
        .setAlertError(true)
        .setAlertSuccess(false)
        .setOnSuccess(() => {
          fetchSavedFilters();
        }),
    );
  }

  /**
   * This method will save the current filter to the API
   */
  async function onSaveFilter() {
    const currentFilters = {
      ...controller.filters,
      columns: JSON.parse(
        localStorage.getItem(controller.config.getSettingsKey()),
      )?.columns,
    };

    await callEvaApi(
      new EvaApiCall("user_saved_filters")
        .setMethod("POST")
        .setLoadingGroup(
          `datatable-${controller.config.getDataTableKey()}-saved-filters-save`,
        )
        .setData({
          key: controller.config.getDataTableKey(),
          name: modalData.name,
          filters: currentFilters,
          default: modalData.default,
        })
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => {
          setSaveModalOpen(false);
          setModalData({ name: "", default: false });
          fetchSavedFilters();
        }),
    );
  }

  /**
   * This method will delete the given filter from the API
   * @param {object} savedFilter
   */
  async function onDeleteFilter(savedFilter) {
    await callEvaApi(
      new EvaApiCall(`user_saved_filters/${savedFilter.id}`)
        .setLoadingGroup(
          `datatable-${controller.config.getDataTableKey()}-saved-filters-delete-${savedFilter.id}`,
        )
        .setMethod("DELETE")
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => {
          if (activeFilter === savedFilter.id) {
            controller.onLoadSavedFilter(null);
            setActiveFilter(null);
          }
          fetchSavedFilters();
        }),
    );
  }

  /**
   * This method will open the save modal
   */
  function onOpenSaveModal() {
    controller.savedFiltersDropdown.setOpen(false);
    setSaveModalOpen(true);
  }

  return (
    <SavedFiltersView
      controller={controller}
      savedFilters={savedFilters}
      activeFilter={activeFilter}
      saveModalOpen={saveModalOpen}
      modalData={modalData}
      setModalData={setModalData}
      onLoadFilter={onLoadFilter}
      onToggleDefault={onToggleDefault}
      onDeleteFilter={onDeleteFilter}
      onOpenSaveModal={onOpenSaveModal}
      onCloseSaveModal={() => setSaveModalOpen(false)}
      onSaveFilter={onSaveFilter}
    />
  );
}

export default SavedFilters;
